import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { offerManagerReducer } from './admin/offer-manager/offerManagerReducer';
import { preownedRatesReducer } from './admin/preownedRates/PreownedAprRatesReducer';
import { tradebookSettingsReducer } from './admin/tradebooks/tradebookSettingsReducer';
import { currentUserReducer } from './current-user/CurrentUserReducer';
import { dealerReducer } from './dealer/DealerReducer';
import { dealsheetReducer } from './dealsheet/DealsheetReducer';
import { digitalAudienceAudienceBuilderReducer } from './digital-audience/DigitalAudienceAudienceBuilderReducer';
import { digitalAudienceDashboardReducer } from './digital-audience/DigitalAudienceDashboardReducer';
import { digitalAudienceReducer } from './digital-audience/DigitalAudienceReducer';
import AuthReducer from './identity/AuthSlice';
import { initialFilterReducer } from './inventory/InitialFilterReducer';
import { inventoryFilterReducer } from './inventory/InventoryFilterReducer';
import { inventoryReducer } from './inventory/InventoryReducer';
import { marketConquestModalReducer } from './market-conquest/MarketConquestModalReducer';
import { marketConquestReducer } from './market-conquest/MarketConquestReducer';
import { notificationsReducer } from './notifications/NotificationReducer';
import { portfolioReducer } from './portfolio/PortfolioReducer';
import { serviceReducer } from './service-portfolio/ServiceReducer';
import { turnDealerReducer } from './turn/dealer/DealerReducer';
import { turnDetailReducer } from './turn/TurnDetailReducer';
import { turnFilterReducer } from './turn/TurnFilterReducer';
import { turnPortfolioReducer } from './turn/TurnReducer';

// Essential UI
import euiContract from './essential-ui/contract/ContractSlice';
import euiDealer from './essential-ui/dealer/DealerSlice';
import euiDealSheet from './essential-ui/dealSheet/DealSheetSlice';
import euiFilters from './essential-ui/filters/FiltersSlice';
import mvoUi from './essential-ui/mvoUI/MvoUiSlice';
import euiOffers from './essential-ui/offers/OffersSlice';
import euiPreferences from './essential-ui/preferences/PreferencesSlice';

import dmsMapperReducer from './admin/dms-mapper/dmsMapperReducer';
import userDealer from './userDealer/UserDealerSlice';
import { digitalAudienceAccountsReducer } from './digital-audience/DigitalAudienceAccountsReducer';
import { digitalAudienceInsightsReducer } from './digital-audience/DigitalAudienceInsightsReducer';

export const store = configureStore({
  reducer: {
    // Authentication
    auth: AuthReducer,

    // Essential UI
    contract: euiContract,
    euiDealer: euiDealer,
    euiDealSheet: euiDealSheet,
    filters: euiFilters,
    mvoUI: mvoUi,
    offers: euiOffers,
    preferences: euiPreferences,

    // User Dealer
    userDealer: userDealer,

    // Acquire
    marketConquest: marketConquestReducer,
    marketConquestModals: marketConquestModalReducer,

    // Digital Audience
    digitalAudience: combineReducers({
      common: digitalAudienceReducer,
      accounts: digitalAudienceAccountsReducer,
      dashboard: digitalAudienceDashboardReducer,
      audienceBuilder: digitalAudienceAudienceBuilderReducer,
      insights: digitalAudienceInsightsReducer,
    }),

    // Dealer
    dealer: dealerReducer,

    // Loyalty Portfolio
    portfolio: portfolioReducer,

    // Service Portfolio
    service: serviceReducer,

    // Notification
    notification: notificationsReducer,

    // Current User
    currentUser: currentUserReducer,

    // Turn
    turnReducer: turnPortfolioReducer,
    turnFilter: turnFilterReducer,
    turnDealer: turnDealerReducer,
    turnDetail: turnDetailReducer,

    // Dealsheet
    dealsheetModernized: dealsheetReducer,

    // Inventory Browser
    inventoryReducer: inventoryReducer,
    inventoryFilter: inventoryFilterReducer,
    initialInventoryFilter: initialFilterReducer,

    // Store Settings
    tradebookSettings: tradebookSettingsReducer,
    offerManager: offerManagerReducer,
    preownedRates: preownedRatesReducer,
    dmsMapper: dmsMapperReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }),
});

export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
